import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Check, X, Maximize2, Ruler, FileImage, FileDown, TrendingUp } from "lucide-react";
import { InquiryForm } from "./InquiryForm";
import { FloorplanDiagram } from "./FloorplanDiagram";
import { AvailabilityBadge } from "./AvailabilityBadge";
import { ShareButtons } from "./ShareButtons";
import { API } from "@/lib/api";

export const ApartmentDetailDialog = ({ apartment, open, onOpenChange, status }) => {
    const [tab, setTab] = useState("details");
    const [zoom, setZoom] = useState(false);
    const [planView, setPlanView] = useState("styled"); // 'styled' | 'original'

    useEffect(() => {
        if (!open) {
            setTab("details");
            setZoom(false);
            setPlanView("styled");
        }
    }, [open]);

    if (!apartment) return null;
    const fp = apartment.floorplan;
    const hasFloorplan = Boolean(fp);
    const hasOriginal = Boolean(fp?.originalImage);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-5xl w-full max-h-[92vh] overflow-y-auto p-0 bg-cream border-stone-200"
                data-testid="apartment-detail-dialog"
            >
                <div className="grid lg:grid-cols-5">
                    <div className="lg:col-span-2 bg-stone-100 relative">
                        <img
                            src={apartment.image}
                            alt={apartment.name}
                            className="w-full h-64 lg:h-full lg:absolute lg:inset-0 object-cover"
                        />
                        <button
                            onClick={() => onOpenChange(false)}
                            className="lg:hidden absolute top-4 right-4 p-2 bg-white/90 backdrop-blur"
                            aria-label="Schließen"
                            data-testid="dialog-close-mobile"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <div className="lg:col-span-3 p-8 lg:p-12">
                        <DialogHeader className="space-y-2 text-left">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <span className="overline">{apartment.position}</span>
                                {status && <AvailabilityBadge status={status} />}
                            </div>
                            <DialogTitle className="font-heading text-3xl lg:text-4xl text-ink leading-tight">
                                {apartment.name}
                            </DialogTitle>
                            <DialogDescription className="font-body text-stone-600">
                                {apartment.rooms} Zimmer · {apartment.size} {apartment.sizeUnit} · {apartment.outdoor}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="mt-6 flex items-baseline gap-3 flex-wrap">
                            <span className="overline text-stone-400">Kaufpreis ab</span>
                            <span className="font-heading text-3xl text-brand-600">
                                {apartment.priceLabel}
                            </span>
                            <span className="font-body text-xs text-stone-500">
                                + Stellplatz {apartment.parkingPrice.toLocaleString("de-DE")} €
                            </span>
                        </div>

                        <div className="mt-2 flex items-baseline gap-2 flex-wrap font-body text-sm text-stone-600">
                            <span>+ ca. 7,5 % Kaufnebenkosten</span>
                            <span className="text-stone-400">·</span>
                            <span>≈ <strong className="text-ink">{Math.round((apartment.priceValue + apartment.parkingPrice) * 0.075).toLocaleString("de-DE")} €</strong></span>
                            <span className="text-stone-400">·</span>
                            <span>
                                Gesamt ca.{" "}
                                <strong className="text-ink">
                                    {Math.round((apartment.priceValue + apartment.parkingPrice) * 1.075).toLocaleString("de-DE")} €
                                </strong>
                            </span>
                        </div>
                        <p className="font-body text-[11px] text-stone-400 mt-1 leading-relaxed">
                            6 % Grunderwerbsteuer Hessen + ca. 1,5 % Notar &amp; Grundbuch.
                            Maklerfrei – Direktverkauf vom Bauträger.
                        </p>

                        <div className="mt-4 flex flex-wrap items-center gap-3">
                            <a
                                href={`${API}/apartments/${apartment.id}/exposee.pdf`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 border border-stone-300 hover:border-brand-500 hover:bg-cream font-body text-sm text-stone-700 transition-colors"
                                data-testid="dialog-download-expose"
                            >
                                <FileDown size={14} />
                                Exposé als PDF
                            </a>
                            <button
                                type="button"
                                onClick={() => {
                                    onOpenChange(false);
                                    window.setTimeout(() => {
                                        window.dispatchEvent(
                                            new CustomEvent("g21:select-rental-apartment", {
                                                detail: { apartmentId: apartment.id },
                                            }),
                                        );
                                        const el = document.getElementById("mietrendite");
                                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                    }, 220);
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 border border-stone-300 hover:border-brand-500 hover:bg-cream font-body text-sm text-stone-700 transition-colors"
                                data-testid="dialog-yield"
                                title="Rendite für diese Wohnung ausrechnen"
                            >
                                <TrendingUp size={14} />
                                Rendite ausrechnen
                            </button>
                        </div>

                        <div className="mt-5 pt-5 border-t border-stone-200">
                            <ShareButtons
                                apartmentId={apartment.id}
                                label="Diese Wohnung teilen"
                            />
                        </div>

                        <div className="mt-8 inline-flex rounded-full border border-stone-200 bg-white overflow-x-auto elegant-scroll">
                            <TabButton active={tab === "details"} onClick={() => setTab("details")} testid="dialog-tab-details">
                                Details &amp; Ausstattung
                            </TabButton>
                            {hasFloorplan && (
                                <TabButton active={tab === "floorplan"} onClick={() => setTab("floorplan")} testid="dialog-tab-floorplan">
                                    <Ruler size={14} />
                                    Grundriss
                                </TabButton>
                            )}
                            <TabButton active={tab === "inquiry"} onClick={() => setTab("inquiry")} testid="dialog-tab-inquiry">
                                Anfrage stellen
                            </TabButton>
                        </div>

                        {tab === "details" && (
                            <div className="mt-8 space-y-8" data-testid="apartment-details-tab">
                                <p className="font-body text-base text-stone-700 leading-relaxed">
                                    {apartment.description}
                                </p>

                                <div>
                                    <h4 className="overline mb-4">Highlights & Ausstattung</h4>
                                    <ul className="grid sm:grid-cols-2 gap-3">
                                        {apartment.highlights.map((h) => (
                                            <li key={h} className="flex items-start gap-3 font-body text-sm text-stone-700">
                                                <Check size={16} className="text-brand-500 mt-0.5 shrink-0" />
                                                <span>{h}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-stone-200 border border-stone-200 rounded-2xl overflow-hidden">
                                    <Stat label="Zimmer" value={apartment.rooms} />
                                    <Stat label="Wohnfläche" value={`${apartment.size} m²`} />
                                    <Stat label="Außen" value={apartment.outdoor} />
                                    <Stat label="Carport" value="Inkl." />
                                </div>

                                <div className="flex flex-wrap gap-3">
                                    {hasFloorplan && (
                                        <button
                                            onClick={() => setTab("floorplan")}
                                            className="btn-secondary flex-1 min-w-[200px]"
                                            data-testid="apartment-go-floorplan"
                                        >
                                            <Ruler size={16} />
                                            Grundriss ansehen
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setTab("inquiry")}
                                        className="btn-primary flex-1 min-w-[200px]"
                                        data-testid="apartment-go-inquiry"
                                    >
                                        Jetzt unverbindliche Anfrage stellen
                                    </button>
                                </div>
                            </div>
                        )}

                        {tab === "floorplan" && hasFloorplan && (
                            <div className="mt-8 space-y-6" data-testid="apartment-floorplan-tab">
                                <div className="flex items-baseline justify-between flex-wrap gap-2">
                                    <div>
                                        <span className="overline">Grundriss · {fp.level}</span>
                                        <h4 className="font-heading text-2xl text-ink mt-1">
                                            {fp.totalSize || `${apartment.size} m²`} Wohnfläche
                                        </h4>
                                    </div>
                                    <button
                                        onClick={() => setZoom(true)}
                                        className="inline-flex items-center gap-2 px-3 py-1.5 border border-stone-300 hover:border-brand-500 text-sm font-body"
                                        data-testid="floorplan-zoom"
                                    >
                                        <Maximize2 size={14} />
                                        Vergrößern
                                    </button>
                                </div>

                                {/* View toggle: styled vs original */}
                                {hasOriginal && (
                                    <div className="inline-flex rounded-full overflow-hidden border border-stone-200 bg-white">
                                        <PlanViewBtn
                                            active={planView === "styled"}
                                            onClick={() => setPlanView("styled")}
                                            testid="planview-styled"
                                        >
                                            <Ruler size={13} />
                                            Schaubild
                                        </PlanViewBtn>
                                        <PlanViewBtn
                                            active={planView === "original"}
                                            onClick={() => setPlanView("original")}
                                            testid="planview-original"
                                        >
                                            <FileImage size={13} />
                                            Original-Bauplan
                                        </PlanViewBtn>
                                    </div>
                                )}

                                <button
                                    onClick={() => setZoom(true)}
                                    className="block w-full rounded-2xl bg-cream border border-stone-200 p-3 sm:p-5 group cursor-zoom-in"
                                    data-testid="floorplan-image-trigger"
                                >
                                    <div className="w-full bg-cream relative overflow-hidden">
                                        {planView === "styled" ? (
                                            <FloorplanDiagram apartmentId={apartment.id} />
                                        ) : (
                                            <img
                                                src={fp.originalImage}
                                                alt={`Original-Grundriss ${apartment.name}`}
                                                className="w-full h-auto object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                                            />
                                        )}
                                    </div>
                                    <p className="font-body text-xs text-stone-500 mt-3 text-center">
                                        Klicken zum Vergrößern
                                    </p>
                                </button>

                                <div>
                                    <h5 className="overline mb-3">Räume im Überblick</h5>
                                    <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                                        {fp.rooms.map((r) => (
                                            <li
                                                key={r.name}
                                                className="flex items-baseline justify-between border-b border-stone-200 py-2"
                                                data-testid={`floorplan-room-${r.name}`}
                                            >
                                                <span className="font-body text-sm text-ink">{r.name}</span>
                                                <span className="font-body text-xs text-stone-500">{r.size}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <p className="font-body text-xs text-stone-500 leading-relaxed border-l-2 border-brand-500 pl-4 py-2">
                                    {fp.note}
                                </p>

                                <button
                                    onClick={() => setTab("inquiry")}
                                    className="btn-primary w-full"
                                    data-testid="floorplan-go-inquiry"
                                >
                                    Anfrage zu dieser Wohnung stellen
                                </button>
                            </div>
                        )}

                        {tab === "inquiry" && (
                            <div className="mt-8" data-testid="apartment-inquiry-tab">
                                <InquiryForm apartment={apartment} onSuccess={() => onOpenChange(false)} />
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>

            {/* Zoom lightbox */}
            {zoom && hasFloorplan && (
                <div
                    className="fixed inset-0 z-[60] bg-stone-900/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10 cursor-zoom-out"
                    onClick={() => setZoom(false)}
                    data-testid="floorplan-lightbox"
                >
                    <button
                        onClick={() => setZoom(false)}
                        className="absolute top-5 right-5 p-3 text-white hover:bg-white/10"
                        aria-label="Schließen"
                        data-testid="floorplan-lightbox-close"
                    >
                        <X size={22} />
                    </button>
                    <div
                        className="bg-cream p-6 sm:p-10 max-w-[95vw] max-h-[92vh] flex flex-col items-center overflow-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="text-center mb-4">
                            <span className="overline text-stone-400">Grundriss · {fp.level}</span>
                            <h4 className="font-heading text-xl text-ink mt-1">{apartment.name}</h4>
                        </div>
                        <div className="w-full max-w-4xl">
                            {planView === "styled" ? (
                                <FloorplanDiagram apartmentId={apartment.id} />
                            ) : (
                                <img
                                    src={fp.originalImage}
                                    alt={`Original-Grundriss ${apartment.name} – vergrößert`}
                                    className="w-full max-h-[70vh] object-contain"
                                />
                            )}
                        </div>
                        <p className="font-body text-xs text-stone-500 mt-4 max-w-2xl text-center">
                            {fp.note}
                        </p>
                    </div>
                </div>
            )}
        </Dialog>
    );
};

const TabButton = ({ active, onClick, testid, children }) => (
    <button
        onClick={onClick}
        className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-body font-medium transition-colors whitespace-nowrap ${
            active ? "bg-ink text-white" : "text-stone-600 hover:text-ink"
        }`}
        data-testid={testid}
    >
        {children}
    </button>
);

const PlanViewBtn = ({ active, onClick, testid, children }) => (
    <button
        onClick={onClick}
        className={`inline-flex items-center gap-2 px-4 py-2 text-xs font-body font-medium tracking-wide transition-colors uppercase ${
            active ? "bg-brand-500 text-white" : "text-stone-600 hover:text-ink"
        }`}
        data-testid={testid}
    >
        {children}
    </button>
);

const Stat = ({ label, value }) => (
    <div className="bg-cream p-4">
        <div className="overline text-stone-400 text-[10px]">{label}</div>
        <div className="font-heading text-lg text-ink mt-1">{value}</div>
    </div>
);
